import { useContext } from 'react';
import { UserContext } from '../../BasicComponents/UserContext';

/**
 * useNextGenSubscription
 *
 * Single source of truth for "does this builder currently have an active
 * paid TSG Nexus subscription?" — used by NextGenLayout (to gate the
 * AI Tutor / Elite Mastery / Mock Interview sidebar items) and by
 * NextGenPaidFeatureGuard (to gate the routes themselves).
 *
 * Deliberately derives this SYNCHRONOUSLY from the builder object already
 * held in context (populated by `subscriptionPlan`/`subscriptionValidTill`
 * on login, and refreshed by NextGenPayment.jsx right after a successful
 * purchase) instead of firing its own GET /api/nextgen-builder/dashboard
 * request on every mount.
 *
 * That earlier design — a live fetch on every NextGenLayout /
 * NextGenPaidFeatureGuard mount — caused a real bug: on a hard reload
 * (e.g. the `window.location.reload()` EliteSelectionModal does after a
 * successful technology pick), this hook's request could race the app's
 * axios interceptor registration and go out without the x-builder-id
 * header, drawing a 400 from nextgenAuthMiddleware. The shared response
 * interceptor treats ANY error containing "Builder session required" as an
 * expired session and clears localStorage — wiping the builder's session
 * mid-navigation and bouncing them to /nextgen-builder/purchase with a
 * crashed page behind it. Reading already-in-memory context data instead
 * removes that request (and the race) entirely.
 */
const useNextGenSubscription = () => {
  const { user } = useContext(UserContext);

  const validTill = user?.subscriptionValidTill || null;
  const isActive = !!(validTill && new Date(validTill) > new Date());

  return {
    loading: false,
    isActive,
    plan: user?.subscriptionPlan || null,
    validTill,
  };
};

export default useNextGenSubscription;
